// Keys msal-browser v2 owns in localStorage, besides the per-account credential entries.
const MSAL_KEY_PREFIXES = ["msal.", "server-telemetry-"];
// Markers that identify a credential/account entry when no specific account is being targeted.
// Every such key is "<homeAccountId>-<environment>-<type>-..." — the credential type and the
// B2C policy name are the reliable tells.
const MSAL_CREDENTIAL_MARKERS = ["-idtoken-", "-accesstoken-", "-refreshtoken-", "-b2c_1"];

/**
 * Delete msal-browser's own localStorage entries.
 *
 * Reaching into another library's storage is not something to do lightly, but the installed
 * msal-browser is v2, which has no local-only removal API: `logoutRedirect` is a NAVIGATION api
 * and calling it during boot races the in-flight `handleRedirectPromise`, rejecting with a plain
 * `BrowserAuthError` that the dev overlay renders as the useless "[object Object]". The
 * alternative was leaving users to clear localStorage by hand, which is what they were doing.
 *
 * Scoped deliberately: with a `homeAccountId` only that account's entries go, and unrelated app
 * state (the chosen `language`, MUI telemetry) always survives — unlike the blanket "clear all".
 *
 * Kept in its own module with no imports so it is directly unit-testable; `storage` is
 * injectable for the same reason. This function deletes user data, so which keys it does and
 * does not take is worth pinning down.
 */
export function removeMsalCacheEntries(homeAccountId?: string, storage?: Storage) {
  try {
    const store = storage ?? window.localStorage;
    const doomed: string[] = [];
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i);
      if (!key) continue;
      if (homeAccountId) {
        // Every credential for an account is prefixed with its homeAccountId.
        if (key.startsWith(homeAccountId)) doomed.push(key);
        continue;
      }
      // Full reset: take MSAL's bookkeeping plus anything shaped like a credential.
      if (
        MSAL_KEY_PREFIXES.some((p) => key.startsWith(p)) ||
        MSAL_CREDENTIAL_MARKERS.some((m) => key.includes(m))
      ) {
        doomed.push(key);
      }
    }
    doomed.forEach((key) => store.removeItem(key));
  } catch (error) {
    console.error("Could not remove the MSAL cache entries:", error);
  }
}
